import React, { useCallback } from 'react';
import { rome as moment } from '../../../../utils/date';
import TableActions from '../../../../shared/table/table-actions';
import { PartnerGuard } from '../../../../guards/RolesGuard';
import { useTheme } from '../../../../utils/theme-context';

const TRIP_STATUSES = [
    { name: 'Completed', value: 'completed', colorName: 'Green', color: 'text-[#10B981]', statusColor: 'bg-[#10B9811A]' },
    { name: 'Scheduled', value: 'scheduled', colorName: 'Amber', color: 'text-[#F59E0B]', statusColor: 'bg-[#F59E0B1A]' },
    { name: 'Cancelled', value: 'cancelled', colorName: 'Red', color: 'text-[#EF4444]', statusColor: 'bg-[#EF44441A]' },
    { name: 'Active', value: 'active', colorName: 'Blue', color: 'text-[#2D8EFF]', statusColor: 'bg-[#2D8EFF1A]' },
    { name: 'In Progress', value: 'in_progress', colorName: 'Blue', color: 'text-[#2D8EFF]', statusColor: 'bg-[#2D8EFF1A]' },
    { name: 'Closed', value: 'closed', colorName: 'Red', color: 'text-[#EF4444]', statusColor: 'bg-[#EF44441A]' },
]

const TripsTable = ({
    rows,
    totalCount,
    hideActions,
    hasMore,
    loadingMore,
    onClickTripDetails,
    onClickEditTrip,
    onClickChangeStatus,
    onClickDelete,
    onLoadMore,
    onExportData,
    scrollContainerRef,
}: {
    rows?: any;
    totalCount?: number;
    hideActions?: boolean;
    hasMore?: boolean;
    loadingMore?: boolean;
    onClickEditTrip?: (data: any) => void;
    onClickTripDetails?: (data: any) => void;
    onClickChangeStatus?: (data: any) => void;
    onClickDelete?: (data: any) => void;
    onLoadMore?: () => void;
    onExportData?: () => void;
    scrollContainerRef?: React.RefObject<HTMLDivElement>;
}) => {
	const { theme } = useTheme();
    const formatStatus = (status: string) => {
        const normalizedStatus = status === 'closed' || status === 'cancelled' || status === 'canceled' ? 'cancelled' : status;
        const findTripStatus = TRIP_STATUSES?.find((item: any) => item?.value === normalizedStatus);
        return (
            <span className={`inline-flex items-center px-3 py-1 w-[72px] text-center justify-center rounded-[4px] text-[11px] font-normal font-arial ${findTripStatus?.statusColor} ${findTripStatus?.color}`}>
                {findTripStatus?.name || status}
            </span>
        );
    }

    const onDownloadReport = (url: string) => {
        window.open(url, '_blank')
    }

    const handleScroll = useCallback(() => {
        if (!scrollContainerRef?.current || !hasMore || loadingMore) return;
        const el = scrollContainerRef.current;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
            onLoadMore?.();
        }
    }, [hasMore, loadingMore, onLoadMore, scrollContainerRef]);

    const gridCols = hideActions
        ? '1.2fr 1.5fr 0.8fr 1fr 0.8fr 0.8fr'
        : '1.2fr 1.5fr 0.8fr 1fr 0.8fr 0.8fr 0.6fr';

    return (
        <>
            {/* ── Header — outside the card ────────────────── */}
            <div className='flex items-center justify-between my-7'>
                <p className='text-[14px] font-normal text-[#717680]'>
                    Found: {totalCount ?? 0} Trips
                </p>
                <div className='flex items-center gap-2'>
                    {onExportData && (
                        <button
                            onClick={onExportData}
                            className='flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition'
                        >
                            Export data
                            <img src='/svg/brand_download.svg' className='w-4 h-4' alt='download icon' />
                        </button>
                    )}
                </div>
            </div>

            {/* ── Table Card ───────────────────────────────── */}
            <div className='border border-gray-200 bg-white dark:bg-[#111317] dark:border-[#252B37] shadow-sm overflow-hidden' style={{ borderRadius: '16px' }}>
                {/* ── Scrollable Rows (header + body) ─────────── */}
                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className='overflow-y-auto'
                    style={{ maxHeight: '600px' }}
                >
                    {/* ── Column Headers ──────────────────────────── */}
                    <div
                        className='grid items-center border-b border-gray-100 px-5 py-3 sticky top-0 z-10'
                        style={{
                            backgroundColor: theme === 'dark' ? '#252B37' : '#FAFAFA',
                            gridTemplateColumns: gridCols,
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '150%',
                            letterSpacing: '0%',
                            color: theme === 'dark' ? '#E2E8F0' : '#414651',
                        }}
                    >
                        <div>Journey</div>
                        <div className='text-center'>Date</div>
                        <div className='text-center'>Train</div>
                        <div className='text-center'>Train Manager</div>
                        <div className='text-center'>Crew</div>
                        <div className='text-center'>Status</div>
                        {!hideActions && <div className='text-center'>Actions</div>}
                    </div>
                    {rows?.map((row: any) => (
                        <div
                            key={row._id}
                            className='grid items-center border-b border-gray-100 px-5 py-3 hover:bg-gray-50 dark:hover:bg-[#232323] transition-colors cursor-pointer'
                            style={{
                                gridTemplateColumns: gridCols,
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '13px',
                            }}
                            onClick={() => onClickTripDetails?.(row)}
                        >
                            {/* Journey */}
                            <div className='text-[#0A0D12] dark:text-white text-[13px] font-medium pr-3'>
                                {row?.itinerary?.journeyTitle || row?.journeyTitle}
                            </div>

                            {/* Date */}
                            <div className='text-[#717680] dark:text-white text-[12px] text-center'>
                                <p>Start: {moment(row?.startDate).format('DD/MM/YYYY, h:mm A')}</p>
                                <p>End: {moment(row?.endDate).format('DD/MM/YYYY, h:mm A')}</p>
                            </div>

                            {/* Train */}
                            <div className='text-[#0A0D12] dark:text-white text-[13px] text-center'>
                                {row?.train?.name || '—'}
                            </div>

                            {/* Train Manager */}
                            <div className='text-[#0A0D12] dark:text-white text-[13px] text-center'>
                                {row?.trainManager?.name || '—'}
                            </div>

                            {/* Crew */}
                            <div className='text-[#0A0D12] dark:text-white text-[13px] text-center'>
                                {row?.assignedCabins?.length || 0} members
                            </div>

                            {/* Status */}
                            <div className='flex items-center justify-center'>
                                {formatStatus(row?.status)}
                            </div>

                            {/* Actions */}
                            {!hideActions && (
                                <div className='flex items-center justify-center' onClick={(e) => e.stopPropagation()}>
                                    <TableActions
                                        containerStyle='!mt-[-10px] !mr-[-20px] !rounded-sm dark:bg-gray-800'>
                                        <div
                                            onClick={() => onClickTripDetails?.(row)}
                                            className='bg-gray-100 dark:bg-gray-800 shadow-xl px-5 py-2 cursor-pointer hover:opacity-70'>
                                            <p className='dark:text-primary-darkMode'>View details</p>
                                        </div>
                                        {!PartnerGuard() &&
                                            <div
                                                onClick={() => onClickEditTrip?.(row)}
                                                className='bg-gray-100 dark:bg-gray-800 shadow-xl px-5 py-2 cursor-pointer hover:opacity-70'>
                                                <p className='text-[#2D8EFF]'>Edit Trip</p>
                                            </div>
                                        }
                                        {!PartnerGuard() &&
                                            <div
                                                onClick={() => onClickChangeStatus?.(row)}
                                                className='bg-gray-100 dark:bg-gray-800 shadow-xl px-5 py-2 cursor-pointer hover:opacity-70'>
                                                <p className='text-[#2D8EFF]'>Change Status</p>
                                            </div>
                                        }
                                        {!PartnerGuard() &&
                                            <div
                                                onClick={() => onClickDelete?.(row)}
                                                className='bg-gray-100 dark:bg-gray-800 shadow-xl px-5 py-2 cursor-pointer hover:opacity-70'>
                                                <p className='text-red-600 dark:text-red-400'>Delete Trip</p>
                                            </div>
                                        }
                                        {row?.reportUrl &&
                                            <div
                                                onClick={() => onDownloadReport(row?.reportUrl)}
                                                className='bg-gray-100 dark:bg-gray-800 shadow-xl px-5 py-2 cursor-pointer hover:opacity-70'>
                                                <p className='text-[#2D8EFF]'>Download Report</p>
                                            </div>
                                        }
                                    </TableActions>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Loading indicator */}
                    {loadingMore && (
                        <div className='flex items-center justify-center py-6'>
                            <svg className='h-5 w-5 animate-spin text-gray-400' viewBox='0 0 24 24' fill='none'>
                                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                                <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4A4 4 0 008 12H4z' />
                            </svg>
                            <span className='ml-2 text-sm text-gray-400'>Loading more...</span>
                        </div>
                    )}

                    {/* No more data */}
                    {!hasMore && rows?.length > 0 && (
                        <div className='text-center py-4 text-xs text-gray-400'>
                            All trips loaded
                        </div>
                    )}

                    {/* Empty state */}
                    {(!rows || rows.length === 0) && (
                        <div className='text-center py-10 text-sm text-gray-400'>
                            No trips found
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default TripsTable;
